import {
  ActionIcon,
  Anchor,
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Menu,
  Pagination,
  Paper,
  Skeleton,
  Table,
  Text,
  Tooltip,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import {
  IconArrowsDiagonal,
  IconCamera,
  IconChecklist,
  IconChevronDown,
  IconChevronRight,
  IconDots,
  IconDragDrop,
  IconEye,
  IconShirt,
  IconSubtask,
  IconTrash,
} from "@tabler/icons-react";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { PrimaryBadge } from "core/components/badge/PrimaryBadge";
import { CountDisplay } from "core/components/countDisplay/CountDisplay";
import { PulseIndicator } from "core/components/index";
import TalentPopover from "core/components/popovers/TalentPopover";
import Th from "core/components/table/Thead";
import { useScrollTracker } from "core/hooks/useScrollTracker";

import { SampleEventType, SampleStatusTypes } from "core/models/SampleModel";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import Moment from "react-moment";
import { Link, useNavigate } from "react-router-dom";

// import { useSortBy, useTable, usePagination, useFilters, useGlobalFilter, useExpanded } from 'react-table';
import { AppConstants, DataFormats, getCompanyTableColumns } from "utils/constants";
import {
  getCommonPinningStyles,
  getStyleStatusColor,
  getUserAvatarColour,
  getUserInitials,
} from "utils/helper";
import { logger } from "utils/logger";
import TextHighlight from "core/components/textHighlight/TextHighlight";

const SCROLL_REF = "distlist-scrollPos";

const DistributionList = ({
  originalData,
  data,
  onItemClick,
  onMenuItemClick,
  onStatusClick,
  onItemSelected,
  selectedItems,
  filter,
  actionMenu,
  columns,
  rowMenuItems,
  showPopover = false,
  allowAssignCapture = false,
  totalCount = 0,
  loading = false,
  paginationSize = 100,
  setFilter,
  showPagination = true,
  user,
}) => {
  const [grouping, setGrouping] = useState([]);
  const [expanded, setExpanded] = useState({ ...filter?.groupExpansion });

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });

  const [columnPinning, setColumnPinning] = useState({
    left: filter?.groupBy?.length ? [] : [getCompanyTableColumns(user.clientCode, 'distributionStyles')[0].accessorKey],
    // right: ["action"],
  });

  useEffect(() => {
    if(user){
      if (filter?.groupBy?.length) {
        setGrouping([filter?.groupBy]);
        setColumnPinning({
          ...columnPinning,
          left: [filter?.groupBy],
        });
      } else {
        setGrouping([]);
        setColumnPinning({
          ...columnPinning,
          left: [getCompanyTableColumns(user.clientCode, 'distributionStyles')[0].accessorKey],
        });
      }
    }
  }, [filter?.groupBy,user]);


  useEffect(() => {
    if (setFilter) setFilter({ ...filter, groupExpansion: { ...expanded } });
  }, [expanded]);

  const tableProps = {
    columns,
    data,
    grouping: grouping,
    setGrouping,
    loading,
    selectedItems,
    pagination,
    setPagination,
    columnPinning,
    onItemClick,
    showPagination,
    expanded,
    setExpanded,
    originalData,
    keyword: filter?.keyword || ""
  };

  const divRef = useRef();
  const { scrolTop } = useScrollTracker(divRef, "distlist");

  // Load the scroll position if it has been previously stored
  useEffect(() => {
    const scrollPosition = localStorage.getItem(SCROLL_REF);

    if (scrollPosition) {
      divRef.current.scrollTo(0, parseInt(scrollPosition, 10));
    } else {
      divRef.current.scrollTo(0, 0);
    }
  }, []);

  return (
    <Box
      ref={divRef}
      style={{ width: "100%", overflowX: "auto", height: "100%" }}
    >
      <CustomTable {...tableProps} />
    </Box>
  );
};

const CustomTable = ({
  columns,
  data,
  grouping,
  setGrouping,
  loading,
  selectedItems,
  onItemClick,
  onMenuItemClick,
  onItemSelected,
  onStatusClick,
  actionMenu,
  showPopover,
  allowAssignCapture,
  pagination,
  setPagination,
  columnPinning,
  showPagination,
  expanded,
  setExpanded,
  originalData,
  keyword
}) => {
  // Use the state and functions returned from useTable to build your UI

  const [scrolled, setScrolled] = useState(false);

  // const groupedRows = table.getPrePaginationRowModel().rows;

  // Then calculate total rows counting expanded groups
  // const getPageCount = (rows) => {
  //   return (rows.length/pagination.pageSize)+1
  // };

  const getPageCount = (rows) => {
    if (!rows.length || !pagination.pageSize) return 0;

    return Math.ceil(rows.length / pagination.pageSize);
  };

  // Calculate pageCount
  // const totalRows = getTotalRows(groupedRows);
  // const pageCount = Math.ceil(totalRows / pageSize);

  const table = useReactTable({
    data: data,
    columns: columns,
    // pageCount: Math.ceil(data.length / pagination.pageSize) ?? -1,
    // pageCount: Math.ceil(getTotalRows(table.getPrePaginationRowModel().rows) / pagination.pageSize),
    rowCount: data.length,
    autoResetExpanded: false,

    // onColumnPinningChange: setColumnPinning,
    // onSortingChange: setSorting,
    // onGroupingChange: setGrouping,
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),

    onGroupingChange: setGrouping,
    onExpandedChange: setExpanded,
    // onPaginationChange: onPagChange,
    state: {
      columnPinning,
      // sorting,
      grouping,
      pagination,
      expanded,
    },
    onPaginationChange: setPagination,
    manualPagination: false,
    debugTable: false,
    defaultColumn: {
      size: 150, //starting column size
      minSize: 100, //enforced during column resizing
      maxSize: 500, //enforced during column resizing
    },
  });

  // logger.log(table.getHeaderGroups())

  const getCell = (cell, row) => {
    var cellRender;

    switch (cell.column.id) {
      case "status": {
        const active =
          cell.getValue() === "active" || cell.getValue() === "true"
            ? true
            : false;
        cellRender = (
          <Badge color={active ? "green" : "gray"}>{cell.getValue()}</Badge>
        );
        break;
      }
      case "otbDate":
      case "distributionDate":
      case "completed": {
        const dateValue = cell.getValue();
        const dateFormats = [
          "YYYY-MM-DD-HH-mm-ss-SSS",
          "YYYY-MM-DD-HH-mm-ss",
        ];
        const isValidDate =
          dateValue && moment(dateValue, dateFormats).isValid();
        cellRender = (
          <Group sx={{ width: 120 }}>
            <Text size="sm">
              {dateValue && dateValue.length ? (
                isValidDate ? (
                  <Moment
                    date={dateValue}
                    parse={dateFormats}
                    format="DD/MM/YYYY"
                    trim
                  />
                ) : (
                  dateValue
                )
              ) : (
                ""
              )}
            </Text>
          </Group>
        );
        break;
      }
      case "styleNumber": {
        const styleValue = cell.getValue();
        cellRender = (
          <Link
            className="router-link"
            to={`/distributions/${styleValue}`}
          >
            {/* <Group justify="space-between"> */}
              <TextHighlight text={styleValue} keyword={keyword} />
            {/* </Group> */}
          </Link>
        );
        break;
      }

      default: {
        const cellValue = cell.getValue();
        const displayValue = typeof cellValue === 'string' || typeof cellValue === 'number' 
          ? cellValue 
          : flexRender(cell.column.columnDef.cell, cell.getContext());
        
        cellRender = (
          <Text size="sm" truncate>
            {typeof displayValue === 'string' || typeof displayValue === 'number' ? (
              <TextHighlight text={displayValue} keyword={keyword} />
            ) : (
              displayValue
            )}
          </Text>
        );
        break;
      }
    }
    return cellRender;
  };

  const numbers = Array(50)
    .fill()
    .map((_, i) => i + 1);

  return (
    <div className="h-scroll-table-container" style={{ height: "100%" }}>
      <Table
        withRowBorders
        withColumnBorders
        highlightOnHover
        size="sm"
        style={{ marginBottom: 0 }}
        className="h-scroll-table"
        stickyHeader
        stickyHeaderOffset={0}
      >
        <Table.Thead
        // className={
        //   scrolled ? ["scrollTableHeader scrolled"] : ["scrollTableHeader"]
        // }
        >
          {table.getHeaderGroups().map((headerGroup, idx) => {
            // const { key, ...headerProps } = { ...headerGroup.getHeaderGroupProps() };
            return (
              <Table.Tr
                key={idx}
                // {...headerProps}
              >
                {headerGroup.headers.map((header, i) => {
                  const { column } = header;
                  const headerWidth =
                    column.id === "expander" ? "auto" : column.width;
                  // const { key, ...colProps } = column.getHeaderProps(column.getSortByToggleProps());
                  return (
                    // Add the sorting props to control sorting. For this example
                    // we can add them into the header props
                    <Th
                      column={column}
                      onSort={() => logger.log("onsort")}
                      key={i}
                      {...{
                        onClick: header.column.getToggleSortingHandler(),
                        reversed:
                          header.column.getIsSorted() === "asc" ? false : true,
                        // onClick: header.column.getToggleGroupingHandler(),
                        style: {
                          cursor: "pointer",
                          minWidth: header.column.getIsGrouped()
                            ? 350
                            : `${header.getSize()}px`,
                        },
                        className: scrolled
                          ? ["scrollTableHeader"]
                          : ["scrollTableHeader", "scrolled"],
                      }}
                      sorted={header.column.getIsSorted() ? true : undefined}
                    >
                      {/* // <Th key={header.id} colSpan={header.colSpan}> */}
                      <div>
                        {header.isPlaceholder ? null : (
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? "cursor-pointer select-none"
                                : "",
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </div>
                        )}
                      </div>
                    </Th>
                  );
                })}
              </Table.Tr>
            );
          })}
        </Table.Thead>

        {loading ? (
          <Table.Tbody>
            {numbers.map((i) => (
              <Table.Tr key={i}>
                {columns.map((col, idx) => (
                  <Table.Td key={idx}>
                    <Skeleton height={8} my="sm" />
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        ) : (
          <Table.Tbody>
            {table.getRowModel().rows.map((row) => {
              let checked = selectedItems.find(
                (val) => val.id === row.original.id
              )
                ? true
                : false;

              return (
                <Table.Tr key={row.id} className={checked ? "selected" : ""}>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <Table.Td
                        key={cell.id}
                        onClick={(e) => {
                          onItemClick(row);
                        }}
                        className={checked ? "cellChecked" : ""}
                        style={{
                          verticalAlign: "top",
                          ...getCommonPinningStyles(cell.column),
                        }}
                      >
                        {cell.getIsGrouped() ? (
                          // If it's a grouped cell, add an expander and row count
                          <>
                            <UnstyledButton
                              size={"sm"}
                              {...{
                                onClick: row.getToggleExpandedHandler(),
                                style: {
                                  cursor: row.getCanExpand()
                                    ? "pointer"
                                    : "normal",
                                },
                              }}
                            >
                              <Group>
                                {/* {row.getIsExpanded() ? '👇' : '👉'}{' '} */}
                                {row.getIsExpanded() ? (
                                  <IconChevronDown
                                    stroke={AppConstants.ICON_STROKE_WIDTH}
                                    size={AppConstants.ICON_SIZE}
                                  />
                                ) : (
                                  <IconChevronRight
                                    stroke={AppConstants.ICON_STROKE_WIDTH}
                                    size={AppConstants.ICON_SIZE}
                                  />
                                )}{" "}
                                {/* <Checkbox
                                  checked={isChecked}
                                  onClick={(e) => {
                                    logger.log(e);
                                    e.stopPropagation();
                                    // e.preventDefault();
                                  }}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    onGroupToggleCheck(
                                      e,
                                      e.currentTarget.checked,
                                      row.subRows
                                    );
                                  }}
                                /> */}
                                {cell.getValue() ? (
                                  // <Text size="sm">{flexRender(
                                  //   cell.column.columnDef.cell,
                                  //   cell.getContext()
                                  // )}</Text>
                                  getCell(cell, row)
                                ) : (
                                  <Text c="dimmed">No value</Text>
                                )}
                                {/* {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}{' '} */}
                                <PrimaryBadge>
                                  {row.subRows.length}
                                </PrimaryBadge>
                              </Group>
                            </UnstyledButton>
                          </>
                        ) : cell.getIsAggregated() ? (
                          // If the cell is aggregated, use the Aggregated
                          // renderer for cell
                          flexRender(
                            cell.column.columnDef.aggregatedCell ??
                              cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        ) : cell.getIsPlaceholder() ? null : ( // For cells with repeated values, render null
                          // Otherwise, just render the regular cell
                          getCell(cell, row)
                          // flexRender(
                          //   cell.column.columnDef.aggregatedCell ??
                          //     cell.column.columnDef.cell,
                          //   cell.getContext()
                          // )
                        )}
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        )}
      </Table>

      {showPagination ? (
        <Paper>
          {loading ? (
            <Group className={"pagination-footer"} py="sm">
              <Skeleton height={20} width={100} />
            </Group>
          ) : (
            <Group className={"pagination-footer"} py="sm">
              <Pagination
                size={"sm"}
                // total={table.getPageCount()}
                total={getPageCount(table.getPrePaginationRowModel().rows)}
                withEdges
                value={table.getState().pagination.pageIndex + 1}
                onChange={(idx) => {
                  logger.log(idx);
                  table.setPageIndex(idx - 1);
                }}
              />

              <Group className="pagination-labels">
                <Text size="sm">
                  Page{" "}
                  <strong>
                    {table.getState().pagination.pageIndex + 1} to{" "}
                    {getPageCount(table.getPrePaginationRowModel().rows)}
                  </strong>{" "}
                   {/* of <strong>{originalData.length}</strong> */}
                </Text>

                {/* <NativeSelect
                              size='sm'
                              value={pageSize}
                              onChange={(e) => {
                                  setPageSize(Number(e.target.value));
                              }}
                              data={[
                                  { value: 20, label: 'Show 20' },
                                  { value: 50, label: 'Show 50' },
                                  { value: 80, label: 'Show 80' },
                                  { value: 100, label: 'Show 100' },
                              ]}></NativeSelect> */}
              </Group>
            </Group>
          )}
        </Paper>
      ) : null}
    </div>
  );
};

export default DistributionList;

const TableRow = ({
  row,
  rowId,
  selectedItems,
  onItemClick,
  onMenuItemClick,
  onStatusClick,
  onItemSelected,
  actionMenu,
  allowAssignCapture = false,
}) => {
  const theme = useMantineTheme();
  const props = {
    row,
    selectedItems,
    onItemClick,
    onMenuItemClick,
    onStatusClick,
    onItemSelected,
    actionMenu,
    allowAssignCapture,
  };
  const navigate = useNavigate();
  // const { key, ...rowProps } = row.getRowProps();

  return (
    <Table.Tr
      key={rowId}
      // {...rowProps}
      className={row.canExpand ? "can-expand" : ""}
    >
      {row.getVisibleCells().map((cell) => {
        const { column } = cell;

        {
          /* {row.cells.map((cell, cellIdx) => { */
        }
        let renderCell;
        const value = cell.column.id;

        let checked = selectedItems.find((val) => val.id === row.original.id)
          ? true
          : false;

        switch (true) {
          case value.includes("expander"): {
            renderCell = (
              <Group
                sx={{
                  maxWidth: row.canExpand ? 60 : 0,
                  minWidth: row.canExpand ? 60 : 0,
                }}
              >
                {/* {cell.render("Cell")} */}
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Group>
            );
            break;
          }
          case value.includes("seasonSort"): {
            renderCell = (
              <Anchor onClick={() => onItemClick(row.original)}>
                {/* {cell.render("Cell")} */}
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Anchor>
            );
            break;
          }
          case value.includes("styleNumber"):
          case value.includes("stylenumber"): {
            renderCell = (
              <Group>
                <Link
                  className="router-link"
                  to={`/distributions/${cell.getValue}`}
                >
                  {/* {cell.render("Cell")} */}
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Link>
                <ActionIcon>
                  <IconArrowsDiagonal />
                </ActionIcon>
              </Group>
            );
            break;
          }

          case value.includes("seasonCode"): {
            renderCell = (
              <div>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            );
            break;
          }

          case value.includes("sessionCode"): {
            renderCell = (
              <div>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            );
            break;
          }
          // case value.includes('photographyCount'):
          case value.includes("styleCount"): {
            renderCell = !cell.row.canExpand ? (
              <Group spacing={"xs"} sx={{ height: "100%", flexShrink: 1 }}>
                <Group wrap="nowrap" spacing={"xs"}>
                  {CountDisplay({
                    val: parseInt(row.original["sampleCount"]),
                    icon: <IconShirt size={18} strokeWidth={1} />,
                    tooltip: "Sample Acquired",
                    theme,
                    onClick: () =>
                      onStatusClick(
                        SampleStatusTypes.SAMPLE_SCAN_COUNT,
                        row.original
                      ),
                  })}
                  {CountDisplay({
                    val: row.original["photographyCount"],
                    icon: <IconCamera size={18} strokeWidth={1} />,
                    tooltip: "Photography Count",
                    onClick: () =>
                      onStatusClick(
                        SampleStatusTypes.SAMPLE_PHOTOGRAPHY_COUNT,
                        row.original
                      ),
                    theme,
                  })}
                </Group>
                <Group wrap="nowrap" spacing={"xs"}>
                  {CountDisplay({
                    val: row.original["taskCount"],
                    icon: <IconSubtask size={18} strokeWidth={1} />,
                    tooltip: "Total Tasks",
                    onClick: () =>
                      onStatusClick(
                        SampleStatusTypes.SAMPLE_TASK_COUNT,
                        row.original
                      ),
                    theme,
                  })}
                  {CountDisplay({
                    val: row.original["taskCompleteCount"],
                    icon: <IconChecklist size={18} strokeWidth={1} />,
                    tooltip: "Tasks Completed",
                    theme,
                  })}
                </Group>
              </Group>
            ) : null;
            break;
          }
          case value.includes("otbDate"):
          case value.includes("distributionDate"):
          case value.includes("completed"): {
            renderCell = (
              <Group sx={{ width: 120 }}>
                {!row.canExpand ? (
                  <Text size="sm">
                    {row.original[value] && row.original[value].length ? (
                      <Moment
                        date={row.original[value]}
                        parse="YYYY-MM-DD-hh-mm-ss"
                        format="DD/MM/YYYY"
                        trim
                      />
                    ) : (
                      ""
                    )}
                  </Text>
                ) : null}
              </Group>
            );
            break;
          }
          case value.includes("status"): {
            renderCell = (
              <div onClick={() => onItemClick(row.original)}>
                {cell.value ? (
                  <Badge color={getStyleStatusColor(cell.value)}>
                    {cell.value}
                  </Badge>
                ) : null}
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </div>
            );
            break;
          }
          case value.includes('instructions["Web Photography Code"]'):
          case value.includes("photoCode"):
          case value.includes("targetSort"): {
            if (row.original.colours) {
              logger.log(rowId);
              renderCell = (
                <Group sx={{ minWidth: "130px", width: "100%" }}>
                  <Group sx={{ flexShrink: 0, flexGrow: 1 }}>
                    {/* <Checkbox
                      checked={
                        selectedItems.find((val) => val.id === row.original.id)
                          ? true
                          : false
                      }
                      onChange={(e) =>
                        onItemSelected(e, row.original, e.currentTarget.checked)
                      }
                    ></Checkbox> */}
                    {/* <SampleCheckBox  element={element} onItemSelected={(elem,selected) => selectHandler(elem,selected)} selectedItems={items} /> */}
                    {/* <Group onClick={() => onItemClick(row.original)}> */}
                    {/* <Image radius='sm' width={50} height={50} src={imgURL} /> */}
                    {/* <IconShirt /> */}
                    {/* </Group> */}
                    {/* <Box sx={{ flexShrink: 1 }} onClick={() => onItemClick(row.original)}> */}
                    <Box sx={{ flexShrink: 0, flexGrow: 1 }}>
                      {/* <SamplePopover
                        data={{ targetSort: row.original.targetSort }}> */}
                      {/* <SamplePopover key={row.original[cell.column.id]} data={{ targetSort: row.original[cell.column.id] }}> */}
                      {/* <Anchor size='sm'> */}
                      <Group position="apart">
                        <Text size="sm" lineClamp={1}>
                          {row.original[cell.column.id]}
                        </Text>

                        {row.original.liveData &&
                        row.original.liveData.files &&
                        row.original.liveData.files.length ? (
                          <Tooltip
                            position={rowId === 0 ? "bottom" : "top"}
                            label={`${moment(
                              row.original.liveData.timestamps.min
                            ).format(DataFormats.TIME_FORMAT)} → ${moment(
                              row.original.liveData.timestamps.max
                            ).format(DataFormats.TIME_FORMAT)}`}
                          >
                            {/* <PulseIndicator /> */}
                            <Box>
                              <PulseIndicator />
                            </Box>
                          </Tooltip>
                        ) : null}
                      </Group>
                      {/* </Anchor> */}
                      {/* </SamplePopover> */}

                      {/* <Text sx={{ maxWidth: 600 }} lineClamp={2} size='sm'>
                                                {row.original.name}
                                            </Text> */}

                      {/* </SamplePopover> */}
                      {/* <Text size='xs' color='dimmed'>
                        {row.original.parentCode}
                      </Text>
                      <Text sx={{ maxWidth: 600 }} lineClamp={2} size='sm'>
                        {row.original.descr}
                      </Text> */}
                    </Box>
                  </Group>
                  {/* {row.original.showIndicator?
                                    <div style={{position:'absolute', top:0, right:0}}>
                                    <LiveIndicator />
                                    </div>
                                    :null} */}
                </Group>
              );
            } else {
              renderCell = cell.row.canExpand ? (
                <Group sx={{ minWidth: "50px" }}>
                  <Group sx={{ flexShrink: 0 }}>
                    {/* <Checkbox
                                            checked={selectedItems.find((val) => val.id === row.original.id) ? true : false}
                                            onChange={(e) => onItemSelected(e, row.original, e.currentTarget.checked)}></Checkbox> */}
                    <Group sx={{ flexShrink: 1 }}>
                      {/* {value.includes('instructions["Web Photography Code"]') ? (
                                                <Text size='sm' lineClamp={1} sx={{ maxWidth: '350px' }}>
                                                    {row.original.instructions['Web Photography Code']}
                                                </Text>
                                            ) : (
                                                <Text size='sm' lineClamp={1} sx={{ maxWidth: '350px' }}>
                                                    {row.original[cell.column.id]}
                                                </Text>
                                            )} */}
                      {row.original.id}
                      {row.original.subRows?.length ? (
                        <Badge color={"brand"}>
                          {row.original.subRows?.length}
                        </Badge>
                      ) : null}
                    </Group>
                  </Group>
                </Group>
              ) : (
                <Group sx={{ minWidth: "50px" }}>
                  <Group sx={{ flexShrink: 0 }}>
                    <Checkbox
                      checked={
                        selectedItems.find((val) => val.id === row.original.id)
                          ? true
                          : false
                      }
                      onChange={(e) =>
                        onItemSelected(e, row.original, e.currentTarget.checked)
                      }
                    ></Checkbox>
                    <Group sx={{ flexShrink: 1 }}>
                      {row.original.instructions
                        ? row.original.instructions["Web Photography Code"]
                        : ""}
                    </Group>
                  </Group>
                </Group>
              );
            }

            break;
          }
          case value.includes("descr"):
          case value.includes("description"): {
            renderCell = (
              <Text
                sx={{ maxWidth: 600, minWidth: 200 }}
                lineClamp={2}
                size="sm"
              >
                {row.original.descr}
              </Text>
            );
            break;
          }
          case value.includes('instructions["Web Stock OTB Date"]'):
          case value.includes("otbDate"): {
            renderCell = (
              <Box sx={{ maxWidth: 200, minWidth: 100 }}>
                {/* <Moment format={DataFormats.DATE_FORMAT_SHORT}>{cell.render('Cell')}</Moment> */}
                {value.includes('instructions["Web Stock OTB Date"]') ? (
                  <Moment format={DataFormats.DATE_FORMAT_SHORT}>
                    {row.original.instructions["Web Stock OTB Date"]}
                  </Moment>
                ) : (
                  cell.render("Cell")
                )}
              </Box>
            );
            break;
          }

          case value.includes("metadata_toplevelcategory"): {
            renderCell = (
              <div onClick={() => onItemClick(row.original)}>
                <Badge color="violet" sx={{ maxWidth: 150 }}>
                  {cell.render("Cell")}
                </Badge>
              </div>
            );
            break;
          }

          case value.includes("instructions"): {
            //const keys = Object.keys(row.original.instructions);
            const regex = /"(.*?)"/; // Regular expression to match the value inside double quotes

            const match = cell.column.id.match(regex);
            let key = match ? match[0] : "";
            key = key.replace(/"/g, "");

            renderCell = (
              <div
                onClick={() => onItemClick(row.original)}
                style={{ minWidth: 80 }}
              >
                {key === "department"
                  ? `${row.original.instructions["Dept"]} - `
                  : ""}
                {row.original.instructions
                  ? row.original.instructions[`${key}`]
                  : ""}

                {/* <Table striped>
                  <tbody>
                  {keys.map((key,idx) => {
                    return (<tr key={idx}>
                      <td style={{width:220}}><Text pl={'sm'} color='dimmed' mr={'md'}>{key}: </Text></td>
                      <td>{row.original.instructions[key]}</td>
                    </tr>)
                  })}
                  </tbody>
                </Table> */}
              </div>
            );
            break;
          }

          case value.includes("issuer"): {
            const userInitials = row.original[value]
              ? getUserInitials(
                  row.original[value].split(" ")[0],
                  row.original[value].split(" ")[1]
                )
              : "";
            const themeCol = getUserAvatarColour(userInitials);

            renderCell = (
              <>
                {row.original[value] ? (
                  <Group wrap="nowrap">
                    <Avatar
                      variant="filled"
                      title={`${row.original[value]}`}
                      color={themeCol}
                      size={30}
                      radius={40}
                    >
                      {row.original[value].charAt(0)}
                    </Avatar>
                    <Text size="sm">{row.original[value]}</Text>
                  </Group>
                ) : null}
              </>
            );
            break;
          }

          case value.includes("action"): {
            // const menuItems = rowMenuItems?rowMenuItems:null;
            renderCell = cell.row.canExpand ? (
              <></>
            ) : (
              <Group
                sx={{
                  width: "100%",
                  alignContent: "center",
                  justifyContent: "end",
                  maxWidth: allowAssignCapture ? "auto" : "auto",
                  minWidth: allowAssignCapture ? "250px" : "auto",
                }}
              >
                {/* {actionMenu ? actionMenu : null} */}
                {allowAssignCapture && row.original.photographyCount ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) =>
                      onMenuItemClick({
                        event: e,
                        type: SampleEventType.SAMPLE_ASSIGN_CAPTURES,
                        data: row.original,
                      })
                    }
                  >
                    Style Guide Selections
                  </Button>
                ) : null}

                <SampleItemMenu
                  {...props}
                  item={row.original}
                  menuItems={actionMenu}
                />
                {/* <SampleItemMenu onMenuItemClick={(e) => onMenuClick(e,row.original)}/> */}
              </Group>
            );
            break;
          }
          case value.includes("studioSessionCodes"): {
            renderCell = (
              <Box style={{ minWidth: 140 }}>
                {row.original.studioSessionCodes?.map((val) => (
                  <div key={val}>
                    <Link to={`/sessions/${val}`} className="router-link">
                      {val}
                      {/* <Badge mb={'xs'} sx={{ cursor: 'pointer' }} onClick={(e) => onMenuItemClick({ event: e, type: SampleEventType.SAMPLE_SHOW_SESSION, data: val })}>
                                            {val}
                                        </Badge> */}
                    </Link>
                  </div>
                ))}
              </Box>
            );
            break;
          }

          case value.includes("talentCodes"): {
            renderCell = (
              <>
                <Avatar.Group spacing="sm" style={{ paddingLeft: 0 }}>
                  {row.original["talents"]?.map((talent, idx) => {
                    const userInitials = talent.fullName
                      ? getUserInitials(
                          talent.fullName.split(" ")[0],
                          talent.fullName.split(" ")[1]
                        )
                      : "";
                    const themeCol = getUserAvatarColour(userInitials);
                    const margin = idx ? -10 : 0;
                    return (
                      <TalentPopover
                        key={idx}
                        data={talent}
                        onShowDetails={() => navigate(`/talent/${talent.code}`)}
                      >
                        <Avatar
                          key={idx}
                          variant="gradient"
                          component={Link}
                          to={`/talent/${talent.code}`}
                          src={talent.latestURL}
                          sx={{ marginLeft: margin }}
                          title={`${talent.fullName}`}
                          color={themeCol}
                          size={25}
                          radius={40}
                        >
                          {talent.fullName?.charAt(0)}
                        </Avatar>
                      </TalentPopover>
                    );
                  })}
                </Avatar.Group>
              </>
            );
            break;
          }

          default: {
            renderCell = cell.row.canExpand ? (
              <></>
            ) : (
              <div style={{ minWidth: 80 }}>
                <Text lineClamp={2} size="sm">
                  {/* {cell.render("Cell")} */}
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Text>
              </div>
            );
          }
        }
        // const { key, ...cellProps } = cell.getCellProps();
        return (
          <Table.Td
            key={cell.id}
            // {...cellProps}
            style={{ verticalAlign: "top", ...getCommonPinningStyles(column) }}
            className={checked ? "cellChecked" : ""}
          >
            {/* <Group sx={{minWidth:150}}> */}
            {renderCell}
            {/* </Group> */}
          </Table.Td>
        );
      })}
    </Table.Tr>
  );
};

const SampleItemMenu = (props) => {
  const defaultMenu = [
    {
      handler: () =>
        props.onMenuItemClick({
          type: SampleEventType.SAMPLE_VIEW,
          data: props.item,
        }),
      icon: <IconEye size={14} />,
      label: "View Sample",
    },
    {
      handler: () =>
        props.onMenuItemClick({
          type: SampleEventType.SAMPLE_ASSIGN_CAPTURES,
          data: props.item,
        }),
      icon: <IconDragDrop size={14} />,
      size: 14,
      label: "Style Guide Selections",
    },
    {
      handler: () =>
        props.onMenuItemClick({
          type: SampleEventType.SAMPLE_DELETE,
          data: props.item,
        }),
      color: "red",
      icon: <IconTrash size={14} />,
      size: 14,
      label: "Unassign Sample",
    },
  ];

  const menuItems = props.actionMenu ? props.actionMenu : defaultMenu;

  return (
    <Menu position="bottom-end">
      <Menu.Target>
        <ActionIcon>
          <IconDots />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {menuItems.map((val, idx) => (
          <Menu.Item
            key={idx}
            onClick={() => val.handler(props.item)}
            icon={val.icon}
          >
            {val.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};
